import { useTranslation } from 'react-i18next';

import Icon from '~/components/icons/Icon';
import type { StockCategory } from '~/types/Product';

interface ProductAvailabilityProps {
  isAvailable: boolean;
  variant: 'icon' | 'dot';
  size?: 'small' | 'large';
  className?: string;
  stock?: number;
  stockCategory?: StockCategory | null;
}

const ProductAvailability = ({
  isAvailable,
  variant,
  size,
  className,
  stock,
  stockCategory,
}: ProductAvailabilityProps) => {
  const { t } = useTranslation();

  // An admin-configured stock category overrides the default in/out-of-stock display.
  // The item count is never shown for a stock category.
  if (stockCategory) {
    return (
      <div
        className={`product-availability product-availability--category product-availability--${size || 'large'} ${className || ''}`}
        style={{ color: stockCategory.color }}
      >
        <span className="product-availability__dot" style={{ backgroundColor: stockCategory.color }} />
        {/* Wrap in an element so the label isn't an anonymous flex item (Firefox drops those on re-render). */}
        <span className="product-availability__label">{stockCategory.name}</span>
      </div>
    );
  }

  const availableClass = isAvailable ? 'is' : 'oos';
  const availabilityText = isAvailable ? t('common.in_stock') : t('common.out_of_stock');
  // Only show the item count when the variant is in stock and a numeric stock value was passed.
  // Counts above 10 are capped to "10+" so we never advertise exact large quantities.
  const stockCount =
    isAvailable && stock !== undefined ? ` ${stock > 10 ? '10+' : stock} ${t('common.piece')}` : '';
  // CSS (.product-availability svg) governs the rendered size; width/height here just mirror
  // the original standalone CheckIcon/XIcon components' intrinsic attributes.
  const availabilityIcon = isAvailable ? (
    <Icon name="check" width={11} height={9} />
  ) : (
    <Icon name="x" aria-hidden="true" focusable="false" />
  );
  return (
    <div
      className={`product-availability product-availability--${availableClass} product-availability--${size || 'large'} ${className || ''}`}
    >
      {variant === 'icon' ? (
        <>
          {availabilityIcon}
          <span className="product-availability__label">
            {availabilityText}
            {stockCount}
          </span>
        </>
      ) : (
        <>
          <span className="product-availability__dot" />
          <span className="product-availability__label">
            {availabilityText}
            {stockCount}
          </span>
        </>
      )}
    </div>
  );
};

export default ProductAvailability;
